import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout, Row, Col, theme, App, Spin, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useEmbed } from '@aseller/embed-sdk/react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { embedLog } from '../embed/embedDebug';
import { setLocale } from '../i18n';
import Calculator from './Calculator';
import type { CalculatorData } from './Calculator';
import CalculatorSummary from './CalculatorSummary';
import BsrChart from './BsrChart';
import SalesPlan from './SalesPlan';
import type { SalesPlanData } from './SalesPlan';
import ForecastAmazonUS from './ForecastAmazonUS';
import type { ForecastSummaryData } from './ForecastAmazonUS';
import Summary from './Summary';
import Budget from './Budget';
import Notes from './Notes';
import Header from './Header';
import { loadResultById, fetchSharedResult } from '../api/client';

import { FISCAL_MONTH_KEYS } from '../utils';

const MONTH_ORDER = FISCAL_MONTH_KEYS;
// monthName в payload — канонический английский ключ ('july', 'august', ...),
// чтобы XLSX-экспорт и шеринг не зависели от UI-локали.
const MONTH_NAMES = FISCAL_MONTH_KEYS;

function mapSalesPlan(raw: any): any {
  if (!raw) return undefined;
  if (Array.isArray(raw.tacos)) return raw;

  const result: any = {};

  if (raw.tacos && typeof raw.tacos === 'object') {
    const entries: {month: number; tacos: number}[] = [];
    for (const [k, v] of Object.entries(raw.tacos)) {
      if (k === 'target') continue;
      const m = parseInt(k.replace('m', ''), 10);
      if (!isNaN(m)) entries.push({ month: m, tacos: Number(v) || 0 });
    }
    entries.sort((a, b) => a.month - b.month);
    result.tacos = entries;
    result.targetTacos = Number(raw.tacos.target) || 0;
  }

  if (raw.annualGrowth && typeof raw.annualGrowth === 'object' && !Array.isArray(raw.annualGrowth)) {
    result.salesGrowth = Object.entries(raw.annualGrowth).map(([y, v]) => {
      const annual = Number(v) || 0;
      const mg = (Math.pow(1 + annual / 100, 1 / 12) - 1) * 100;
      return { year: Number(y), annualGrowth: annual, monthlyGrowth: +mg.toFixed(2) };
    }).sort((a, b) => a.year - b.year);
  }

  if (raw.monthlyPlan && typeof raw.monthlyPlan === 'object') {
    result.monthlyGrowth2025 = MONTH_ORDER.map((key, i) => ({
      month: i + 1,
      monthName: MONTH_NAMES[i],
      growth: Number((raw.monthlyPlan as any)[key]) || 0,
    }));
  }

  return result;
}

function mapCalcData(raw: Record<string, unknown> | undefined) {
  if (!raw) return undefined;
  return {
    purchasePrice: raw.purchase_price as number,
    shippingCostPerKg: raw.shipping_cost_per_kg as number,
    unitWeightKg: raw.unit_weight_kg as number,
    storageFee: (raw.storage_fee ?? raw.storage_fee_per_unit) as number,
    sellingPrice: raw.selling_price as number,
    referralFee: raw.referral_fee as number,
    fbaFee: raw.fba_fee as number,
    otherFees: raw.other_fees as number,
    ppcCost: raw.ppc_cost as number,
    conversionRate: raw.conversion_rate as number,
    ppcSalesPercentage: raw.ppc_sales_percentage as number,
    returnRate: raw.return_rate as number,
    resellableReturns: raw.resellable_returns as number,
    monthlySales: raw.monthly_sales as number,
    batchQuantity: raw.batch_quantity as number,
    couponPromoEnabled: raw.coupon_promo_enabled as boolean ?? true,
    asin: (raw.parent_asin as string) || undefined,
    competitorAsins: Array.isArray(raw.competitors)
      ? (raw.competitors as { asin: string }[]).map((c) => c.asin)
      : undefined,
  };
}

const EmbedView: React.FC = () => {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { t } = useTranslation();
  // Origins берём из RUNTIME-конфига (docker-entrypoint инжектит config.js),
  // а не из import.meta.env — build-time VITE_ALLOWED_ORIGINS в проде пуст, и
  // SDK-мост с пустым списком отвергал бы ВСЕ postMessage (AUTH/INIT) →
  // embed.isAuthenticated навсегда false. CU-869dne01h: без '*' fallback.
  const _rc = (window as unknown as { __RUNTIME_CONFIG__?: Record<string, string> }).__RUNTIME_CONFIG__;
  const _embedOriginsRaw = _rc?.ALLOWED_ORIGINS || import.meta.env.VITE_ALLOWED_ORIGINS || '';
  const embed = useEmbed({
    toolName: 'idea-calc',
    allowedOrigins: _embedOriginsRaw.split(',').map((s: string) => s.trim()).filter(Boolean),
  });

  // CU-869dne01h: ждём embed.ready перед применением locale от SDK.
  // Раньше SDK инициализировался с дефолтом 'en' (truthy) → setLocale('en')
  // срабатывал на первом рендере ДО INIT от Hub → затирал сохранённую
  // пользователем локаль в localStorage. Старый Hub без поддержки locale →
  // юзер навсегда оказывался на 'en' с flash-of-English.
  useEffect(() => {
    if (embed.ready && embed.locale) setLocale(embed.locale);
  }, [embed.ready, embed.locale]);
  const [calculatorData, setCalculatorData] = useState<CalculatorData | undefined>();
  const [salesPlanData, setSalesPlanData] = useState<SalesPlanData | undefined>();
  const [forecastSummary, setForecastSummary] = useState<ForecastSummaryData | null>(null);
  const [asin, setAsin] = useState('');
  const [competitorAsins, setCompetitorAsins] = useState<string[]>([]);
  const [keepaLoaded, setKeepaLoaded] = useState(false);
  const [loadedResult, setLoadedResult] = useState<Record<string, unknown> | null>(null);
  const [loadKey, setLoadKey] = useState(0);
  const [loadedFileName, setLoadedFileName] = useState('');
  const [loadedResultId, setLoadedResultId] = useState<number | null>(null);
  const [budgetData, setBudgetData] = useState<any>(undefined);
  const [notesData, setNotesData] = useState<string>('');
  // Identity из SDK: aseller шлёт AUTH → embed.userId (JWT _id), Hub шлёт INIT
  // → embed.user.id. Кладём в стор → api-клиент отправляет X-User-Id, backend
  // ему доверяет. Обмена на Keycloak JWT нет (batch CU-869dt3dz6 откат).
  useEffect(() => {
    const id = embed.userId || embed.user?.id;
    if (id) {
      embedLog('setEmbedUser: userId=', id);
      useAuthStore.getState().setEmbedUser(id);
    }
  }, [embed.userId, embed.user]);

  // Трейс состояния SDK — видно mode-detection, handshake и где застряли.
  useEffect(() => {
    embedLog('embed state', {
      mode: embed.mode,
      embedded: embed.embedded,
      ready: embed.ready,
      isAuthenticated: embed.isAuthenticated,
      userId: embed.userId,
      authTimedOut: embed.authTimedOut,
      parentOrigin: embed.parentOrigin,
    });
  }, [embed.mode, embed.embedded, embed.ready, embed.isAuthenticated, embed.userId, embed.authTimedOut, embed.parentOrigin]);

  // Auto-resize iframe — observe #root (React mount point).
  // We pin antd popups into #root via ConfigProvider.getPopupContainer in
  // App.tsx; combined with overflow-x:hidden below, hovering tooltips at
  // the right edge can't cause horizontal scroll bars that would reflow
  // content and trigger spurious RESIZE events.
  useEffect(() => {
    if (!embed.embedded) return;
    // Prevent right-edge tooltips from inducing a scrollbar that changes layout
    const prevOverflowX = document.body.style.overflowX;
    document.body.style.overflowX = 'hidden';
    const root = document.getElementById('root') ?? document.body;
    let lastSent = 0;
    let timer: number | null = null;
    const schedule = (height: number) => {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        if (Math.abs(height - lastSent) <= 16) return;
        lastSent = height;
        embed.sendResize(height);
      }, 100);
    };
    const observer = new ResizeObserver(([entry]) => {
      schedule(Math.ceil(entry.contentRect.height));
    });
    observer.observe(root);
    return () => {
      if (timer !== null) window.clearTimeout(timer);
      observer.disconnect();
      document.body.style.overflowX = prevOverflowX;
    };
  }, [embed.embedded, embed.sendResize]);

  // Send output data to parent when forecast changes
  useEffect(() => {
    if (forecastSummary) {
      embed.sendOutput({ forecastSummary, calculatorData });
    }
  }, [forecastSummary, calculatorData]);

  const handleCalcChange = useCallback((data: CalculatorData) => {
    setCalculatorData(data);
    if (data.asin) setAsin(data.asin);
    else setAsin('');
    if (data.competitorAsins?.length) setCompetitorAsins(data.competitorAsins);
    setKeepaLoaded(!!data.keepaLoaded);
  }, []);

  const handleLoadResult = useCallback(async (idOrShare: number | string) => {
    try {
      const data = typeof idOrShare === 'number'
        ? await loadResultById(idOrShare)
        : await fetchSharedResult(idOrShare); // share-токен
      setLoadedResult(data);
      setLoadKey((k) => k + 1);
      setLoadedFileName((data.fileName as string) || '');
      setLoadedResultId(typeof data.id === 'number' ? data.id : null);
    } catch {
      message.error(t('idea_calc.messages.load_calc_error'));
    }
  }, []);

  const calcInitial = useMemo(
    () => loadedResult ? mapCalcData(loadedResult.calculation_data as Record<string, unknown>) : undefined,
    [loadedResult],
  );
  const budgetInitial = useMemo(() => loadedResult?.budget_data as any, [loadedResult]);
  const salesPlanInitial = useMemo(() => mapSalesPlan(loadedResult?.sales_plan), [loadedResult]);
  const notesInitial = useMemo(() => loadedResult?.notes as string | undefined, [loadedResult]);

  // Calculate total budget from budget categories (matching DashboardView logic)
  const totalLaunchBudget = useMemo(() => {
    if (!budgetData) return 15000;
    const cogs = calculatorData ? calculatorData.purchasePrice + calculatorData.shippingPrice : 5;
    // Full unit cost for buyouts: COGS + FBA + referral fee + other fees + storage
    const fullUnitCost = calculatorData ? (
      calculatorData.purchasePrice +
      calculatorData.shippingPrice +
      calculatorData.fbaFee +
      (calculatorData.sellingPrice * calculatorData.referralFee / 100) +
      (calculatorData.sellingPrice * calculatorData.otherFees / 100) +
      calculatorData.storageFee
    ) : 0;
    return budgetData.reduce((sum: number, cat: any) => {
      return sum + cat.items.reduce((catSum: number, item: any) => {
        if (item.marketingType === 'vine') {
          return catSum + (item.quantity || 0) * cogs + (item.vinePrice || 0);
        }
        if (item.marketingType === 'buyout' || item.marketingType === 'reviews') {
          // Formula: quantity × (servicePrice + fullUnitCost) × (1 + commissionPercent%)
          const perUnit = (item.unitCost || 0) + fullUnitCost;
          const subtotal = (item.quantity || 0) * perUnit;
          return catSum + subtotal + subtotal * ((item.commissionPercent || 0) / 100);
        }
        return catSum + (item.amount || 0);
      }, 0);
    }, 0);
  }, [budgetData, calculatorData]);

  // Auth gate: hub ждёт INIT (ready), aseller — AUTH-handshake SDK
  // (isAuthenticated). Токен-обмена нет: identity уходит на backend как
  // X-User-Id. Требует корректных allowedOrigins (см. runtime-конфиг выше),
  // иначе SDK отвергнет AUTH и isAuthenticated не выставится.
  const isReady = embed.mode === 'hub' ? embed.ready : embed.isAuthenticated;
  if (!isReady) {
    // In Hub embed mode iframe auto-resizes to body height; using `100vh` here
    // would emit a RESIZE that grows the iframe to full viewport, pushing the
    // spinner off-screen. Use a small fixed height so the spinner stays at
    // the top of the iframe area.
    const wrapperHeight = embed.embedded ? 320 : '100vh';
    return (
      <div style={{ minHeight: wrapperHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        {embed.authTimedOut ? (
          <Typography.Text type="secondary">{t('idea_calc.embed.auth_required')}</Typography.Text>
        ) : (
          <>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} />} />
            <Typography.Text type="secondary">
              {embed.mode === 'hub' ? t('idea_calc.embed.connecting_hub') : t('idea_calc.embed.waiting_auth')}
            </Typography.Text>
          </>
        )}
      </div>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      <Header calculatorData={calculatorData} salesPlanData={salesPlanData} budgetData={budgetData} notesData={notesData} onLoadResult={handleLoadResult} loadedFileName={loadedFileName} loadedResultId={loadedResultId} />
      <Layout.Content
        style={{
          maxWidth: 1400,
          width: '100%',
          margin: '0 auto',
          padding: token.paddingSM,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div id="calculator">
            <Row gutter={[12, 12]} align="stretch">
              <Col xs={24} lg={6} style={{ display: 'flex' }}>
                <Calculator key={`calc-${loadKey}`} onDataChange={handleCalcChange} initialData={calcInitial} />
              </Col>
              <Col xs={24} lg={11} style={{ display: 'flex' }}>
                <div id="budget" style={{ display: 'flex', width: '100%' }}>
                  <Budget
                    key={`budget-${loadKey}`}
                    cogs={calculatorData ? calculatorData.purchasePrice + calculatorData.shippingPrice : undefined}
                    fullUnitCost={calculatorData ? (
                      calculatorData.purchasePrice +
                      calculatorData.shippingPrice +
                      calculatorData.fbaFee +
                      (calculatorData.sellingPrice * calculatorData.referralFee / 100) +
                      (calculatorData.sellingPrice * calculatorData.otherFees / 100) +
                      calculatorData.storageFee
                    ) : undefined}
                    initialData={budgetInitial}
                    onDataChange={setBudgetData}
                  />
                </div>
              </Col>
              <Col xs={24} lg={7} style={{ display: 'flex' }}>
                <CalculatorSummary data={calculatorData} launchBudget={totalLaunchBudget} couponPromoEnabled={calculatorData?.couponPromoEnabled} />
              </Col>
            </Row>
          </div>
          {asin && keepaLoaded && <BsrChart asin={asin} />}
          <div id="sales-plan">
            <SalesPlan
              key={`sp-${loadKey}`}
              onDataChange={setSalesPlanData}
              asin={asin}
              competitorAsins={competitorAsins}
              purchasePrice={calculatorData?.purchasePrice}
              initialData={salesPlanInitial}
            />
          </div>
          <div id="forecast">
            <ForecastAmazonUS
              calculatorData={calculatorData}
              salesPlanData={salesPlanData}
              launchBudget={totalLaunchBudget}
              onSummaryChange={setForecastSummary}
            />
          </div>
          <div id="summary">
            <Summary forecastSummary={forecastSummary} />
          </div>
          <div id="notes">
            <Notes key={`notes-${loadKey}`} initialNotes={notesInitial} onDataChange={setNotesData} />
          </div>
        </div>
      </Layout.Content>
{/* History is now accessible via Header */}
    </Layout>
  );
};

export default EmbedView;
